<template>
  <div class="flex items-center gap-2">
    <input
      :id="props.context.id"
      :name="props.context.name"
      class="relative rounded-full shadow-sm appearance-none cursor-pointer toggle-switch bg-slate-300 focus:outline-none border-none"
      :class="classesString"
      :disabled="props.context.disabled"
      :value="props.context._value"
      :checked="props.context._value"
      :data-testid="dataTestid"
      @input="handleInput"
      @click="handleClick"
      v-bind="props.context.$attrs"
      type="checkbox"
      role="switch"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useClassName } from './../../composables';

interface Context {
  id: string;
  name: string;
  disabled: boolean;
  _value: boolean;
  $attrs: Record<string, any>;
  dataTestid?: string;
  attrs?: Record<string, any>;
  node: {
    input: (value: boolean) => void;
  };
  size?: 'normal' | 'small';
  variant?: 'primary' | 'success' | 'warning' | 'danger';
}

const props = defineProps<{
  context: Context;
  size?: 'normal' | 'small';
  variant?: 'primary' | 'success' | 'warning' | 'danger';
}>();

const dataTestid = computed(() => {
  return String(props.context.dataTestid || props.context.attrs?.['data-testid'] || '');
});

const handleInput = ($e: Event) => {
  const target = $e.target as HTMLInputElement | null;
  if (target) {
    props.context.node.input(target.checked);
  }
};

const handleClick = ($e: Event) => {
  const { $attrs } = props.context;
  if ($attrs && $attrs.onClick && $attrs.onClick instanceof Function) {
    $attrs.onClick($e);
  }
};

const { getClassName } = useClassName();

const classesString = getClassName({
  small: props.context.size === 'small',
  normal: props.context.size !== 'small',
  disabled: props.context.disabled,
  success: props.context.variant === 'success',
  warning: props.context.variant === 'warning',
  danger: props.context.variant === 'danger',
  primary: props.context.variant === 'primary',
});
</script>

<style scoped>
input.normal {
  @apply h-5 w-9;
}

input.normal.toggle-switch::after {
  @apply w-3 h-3 top-1 left-1;
}

input.normal.toggle-switch:checked::after {
  @apply right-1;
}

input.small {
  @apply h-3 w-5;
}

input.small.toggle-switch::after {
  @apply h-2 w-2 top-0.5 left-0.5;
}

input.small.toggle-switch:checked::after {
  @apply right-0.5;
}

input.toggle-switch::after {
  @apply content-[''] bg-white rounded-full absolute;
}
input.primary.toggle-switch:checked {
  @apply bg-blue-500;
}

input.success.toggle-switch:checked {
  @apply bg-green-500;
}

input.warning.toggle-switch:checked {
  @apply bg-orange-500;
}

input.danger.toggle-switch:checked {
  @apply bg-red-500;
}

input.toggle-switch:checked::after {
  @apply left-auto;
}

label.small {
  @apply text-sm;
}

input.success.toggle-switch:checked + label.success {
  @apply text-green-500;
}

input.warning.toggle-switch:checked + label.warning {
  @apply text-orange-500;
}

input.danger.toggle-switch:checked + label.danger {
  @apply text-red-500;
}

.disabled {
  cursor: not-allowed;
}

[type='checkbox']:checked {
  background-image: none;
}
</style>
